import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  closeAdminIaDiscussion,
  fetchAdminIaDiscussions,
  fetchAdminIaMessages,
  fetchIaStats,
} from "../../../REDUX/actions/iaAdmin";
import { renderIaMarkdown } from "../iaMarkdown";

const money = (value) => `$${(Number(value) || 0).toFixed(4)}`;

/**
 * Consultation des conversations de tous les utilisateurs, avec le cout reel
 * de chaque message et de chaque discussion.
 */
const IaAdminConversations = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const {
    discussions,
    discussionsLoading,
    openedDiscussionId,
    messages,
    messagesLoading,
    stats,
  } = useSelector((state) => state.iaAdminReducer);

  useEffect(() => {
    dispatch(fetchAdminIaDiscussions());
    dispatch(fetchIaStats());
  }, [dispatch]);

  const opened = discussions.find((d) => d.id === openedDiscussionId);

  if (openedDiscussionId) {
    const totalFromMessages = messages.reduce(
      (sum, message) => sum + (Number(message.costUsd) || 0),
      0
    );

    return (
      <div className="d-flex flex-column gap-3">
        <button
          type="button"
          className="border-0 bg-transparent f14 iaBlue alignH gap-2 p-0 wfitCon"
          onClick={() => dispatch(closeAdminIaDiscussion())}
        >
          <i className="bi bi-arrow-left" />
          {t("Back to conversations")}
        </button>

        <div className="iaAdminCard br5 bg-lightGrey p-3 d-flex flex-wrap gap-4">
          <div>
            <p className="f12 normal text-lightBlack mb-1">{t("Conversation")}</p>
            <p className="f14 sBold text-lightBlack mb-0">
              {opened?.title || `#${openedDiscussionId}`}
            </p>
          </div>
          <div>
            <p className="f12 normal text-lightBlack mb-1">{t("User")}</p>
            <p className="f14 sBold text-lightBlack mb-0">{opened?.user?.name || "-"}</p>
          </div>
          <div>
            <p className="f12 normal text-lightBlack mb-1">{t("Messages")}</p>
            <p className="f14 sBold text-lightBlack mb-0">{messages.length}</p>
          </div>
          <div>
            <p className="f12 normal text-lightBlack mb-1">{t("Total cost")}</p>
            <p className="f14 sBold iaBlue mb-0">{money(totalFromMessages)}</p>
          </div>
        </div>

        {messagesLoading ? (
          <span className="f14 normal text-lightBlack">{t("Loading")}...</span>
        ) : (
          <div className="d-flex flex-column gap-3">
            {messages.map((message) => {
              const isUser = message.role === "user";

              return (
                <div
                  key={message.id}
                  className={`iaAdminMessage br5 p-3 ${
                    isUser ? "bg-lightBlue" : "bg-lightGrey"
                  }`}
                >
                  <div className="alignH justify-content-between mb-2">
                    <span className="f12 sBold iaBlue">
                      {isUser ? t("User") : "Poseidon"}
                    </span>
                    <span className="f11 normal text-lightBlack">
                      {message.createdAt
                        ? moment(message.createdAt).format("DD/MM/YYYY HH:mm")
                        : ""}
                    </span>
                  </div>

                  {isUser ? (
                    <p className="f13 normal text-lightBlack mb-0 iaUserText">
                      {message.content}
                    </p>
                  ) : (
                    <div
                      className="f13 normal iaMarkdown"
                      dangerouslySetInnerHTML={{
                        __html: renderIaMarkdown(message.content),
                      }}
                    />
                  )}

                  {message.errorMessage && (
                    <p className="f12 normal text-danger mb-0 mt-2">
                      {message.errorMessage}
                    </p>
                  )}

                  {/* Visible ici seulement : l'utilisateur ne recoit jamais les
                      sources, mais l'administrateur en a besoin pour savoir sur
                      quels documents l'assistant s'est appuye. */}
                  {!isUser && message.sources?.length > 0 && (
                    <div className="mt-2">
                      <p className="f11 sBold text-lightBlack mb-1">
                        {t("Documents used")} ({message.sources.length})
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {message.sources.map((source, index) => (
                          <span
                            key={`${source.kind}-${source.iaDocumentId || source.url}-${index}`}
                            className="iaBadge iaBadgeMuted"
                            title={source.url || source.fileName}
                          >
                            {source.kind === "web"
                              ? `web · ${source.host || source.title}`
                              : source.fileName || source.title}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isUser && (
                    <div className="alignH gap-3 flex-wrap mt-2 pt-2 border-top">
                      <span className="f11 normal text-lightBlack">
                        {t("Model")}: <strong>{message.model || "-"}</strong>
                      </span>
                      <span className="f11 normal text-lightBlack">
                        {t("Input")}: {message.inputTokens || 0}
                        {message.cachedInputTokens
                          ? ` (${message.cachedInputTokens} ${t("cached")})`
                          : ""}
                      </span>
                      <span className="f11 normal text-lightBlack">
                        {t("Output")}: {message.outputTokens || 0}
                      </span>
                      <span className="f11 normal text-lightBlack">
                        file_search: {message.fileSearchCalls || 0}
                      </span>
                      <span className="f11 normal text-lightBlack">
                        web_search: {message.webSearchCalls || 0}
                      </span>
                      <span className="f12 sBold iaBlue ms-auto">
                        {money(message.costUsd)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-4">
      {stats && (
        <div className="d-flex flex-wrap gap-3">
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Questions")}</p>
            <p className="f20 sBold iaBlue mb-0">{stats.questions || 0}</p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Total cost")}</p>
            <p className="f20 sBold iaBlue mb-0">{money(stats.totals?.costUsd)}</p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Average per question")}</p>
            <p className="f20 sBold iaBlue mb-0">{money(stats.averageCostUsd)}</p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">
              {t("Average per conversation")}
            </p>
            <p className="f20 sBold iaBlue mb-0">
              {money(stats.averageCostPerDiscussionUsd)}
            </p>
            <p className="f11 normal text-lightBlack mb-0">
              {stats.discussions || 0} {t("conversations")} ·{" "}
              {stats.averageQuestionsPerDiscussion || 0} {t("questions each")}
            </p>
          </div>
          <div className="iaStatCard br5 bg-lightGrey p-3">
            <p className="f12 normal text-lightBlack mb-1">{t("Web searches")}</p>
            <p className="f20 sBold iaBlue mb-0">{stats.totals?.webSearchCalls || 0}</p>
          </div>
          {stats.totals?.errors > 0 && (
            <div className="iaStatCard br5 bg-lightGrey p-3">
              <p className="f12 normal text-lightBlack mb-1">{t("Failed answers")}</p>
              <p className="f20 sBold text-danger mb-0">{stats.totals.errors}</p>
            </div>
          )}
        </div>
      )}

      {discussionsLoading ? (
        <span className="f14 normal text-lightBlack">{t("Loading")}...</span>
      ) : (
        <div className="table-responsive">
          <table className="iaTable w100 f13">
            <thead>
              <tr>
                <th>{t("User")}</th>
                <th>{t("Conversation")}</th>
                <th>{t("Last message")}</th>
                <th>{t("Input")}</th>
                <th>{t("Output")}</th>
                <th>{t("Total cost")}</th>
              </tr>
            </thead>
            <tbody>
              {discussions.map((discussion) => (
                <tr
                  key={discussion.id}
                  className="iaClickableRow"
                  onClick={() => dispatch(fetchAdminIaMessages(discussion.id))}
                >
                  <td>{discussion.user?.name || "-"}</td>
                  <td>{discussion.title || `#${discussion.id}`}</td>
                  <td>
                    {discussion.lastMessageAt
                      ? moment(discussion.lastMessageAt).format("DD/MM/YYYY HH:mm")
                      : "-"}
                  </td>
                  <td>{discussion.totalInputTokens || 0}</td>
                  <td>{discussion.totalOutputTokens || 0}</td>
                  <td className="sBold iaBlue">{money(discussion.totalCostUsd)}</td>
                </tr>
              ))}

              {!discussions.length && (
                <tr>
                  <td colSpan={6} className="text-center text-lightBlack py-3">
                    {t("No conversation yet")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IaAdminConversations;
